import { Button, Form, Input } from 'antd'
import { useEffect, useRef, useState } from 'react'
import api from 'src/api/index.api'
import { ButtonCommon } from 'src/common'
import { useAppDispatch, useAppSelector } from 'src/hooks'
import { onReloadDefinitionWithoutLoading, onUpdateDictWord } from 'src/redux/definition/definition.actions'
import {
	selectCurrentDictWord,
	selectDefinitionEN,
	selectDefinitionVN,
} from 'src/redux/definition/definition.selector'
import { onGetAllDictWordTypes } from 'src/redux/dict/dict.action'
import { selectDictWordTypes } from 'src/redux/dict/dict.selector'
import { DICT_LANGUAGE } from 'src/utils/constant'
import { onCreateDictWord } from 'src/redux/dict/dict.action'
import { showMessageError, showMessageSuccess } from 'src/utils/function.utils'

const FormEditWord = () => {
	const dispatch = useAppDispatch()
	const dictWord = useAppSelector(selectCurrentDictWord)
	const definitionVN = useAppSelector(selectDefinitionVN)
	const definitionEN = useAppSelector(selectDefinitionEN)
	const dictWordTypes = useAppSelector(selectDictWordTypes)
	const [form] = Form.useForm()
	const inputRef = useRef()

	const [isSubmitLoading, setIsSubmitLoading] = useState()
	const [isGeneratingSpelling, setIsGeneratingSpelling] = useState(false)
	const [isGeneratingAll, setIsGeneratingAll] = useState(false)

	useEffect(() => {
		inputRef.current.focus()
	}, [])

	useEffect(() => {
		if (dictWord?.id) {
			form.setFieldsValue({
				word: dictWord.word,
				spelling: dictWord.spelling,
				spellingUk: dictWord.spellingUk,
			})
		}
	}, [dictWord, form])

	useEffect(() => {
		if (!dictWordTypes || dictWordTypes.length <= 0) {
			dispatch(onGetAllDictWordTypes({}))
		}
	}, [dispatch, dictWordTypes])

	const handleSubmit = async () => {
		const values = await form.validateFields()
		const word = (values.word || '').trim()
		const spelling = (values.spelling || '').trim()
		const spellingUk = (values.spellingUk || '').trim()

		if (word) {
			setIsSubmitLoading(true)
			const payload = { word, spelling, spellingUk }
			if (dictWord?.id) {
				dispatch(
					onUpdateDictWord(
						{
							id: dictWord.id,
							...payload,
						},
						handleSubmitCb
					)
				)
			} else {
				dispatch(onCreateDictWord(payload, handleSubmitCb))
			}
		} else {
			showMessageError('Please enter word')
		}
	}

	const handleSubmitCb = (isSuccess, rs) => {
		setIsSubmitLoading(false)
		if (isSuccess) {
			showMessageSuccess()
		} else {
			showMessageError(rs)
		}
	}

	const getSpellingByType = (rs, type) => {
		const usValue =
			rs?.spelling ||
			rs?.Spelling ||
			rs?.spellingUs ||
			rs?.spellingUS ||
			rs?.us ||
			''
		const ukValue =
			rs?.spellingUk ||
			rs?.spellingUK ||
			rs?.SpellingUk ||
			rs?.uk ||
			''
		return type === 'uk' ? ukValue : usValue
	}

	const handleGenerateBothSpellings = async () => {
		const word = (form.getFieldValue('word') || '').trim()
		if (!word) {
			showMessageError('Please enter word first')
			return
		}
		try {
			setIsGeneratingSpelling(true)
			const rs = await api.lookupWordApi.getSpellingWord(word)
			const spelling = getSpellingByType(rs, 'us')
			const spellingUk = getSpellingByType(rs, 'uk')
			if (!spelling && !spellingUk) {
				showMessageError('Cannot generate pronunciation for this word')
				return
			}
			form.setFieldsValue({
				spelling: spelling || form.getFieldValue('spelling'),
				spellingUk: spellingUk || form.getFieldValue('spellingUk'),
			})
			showMessageSuccess('Generated pronunciation successfully')
		} catch (err) {
			showMessageError(
				err?.response?.data || err?.message || 'Failed to generate pronunciation'
			)
		} finally {
			setIsGeneratingSpelling(false)
		}
	}

	const getDefaultTypeByLang = lang => {
		return (dictWordTypes || []).find(item => item?.isTypeLanguage === lang)
	}

	const getExistingTypeByLang = lang => {
		const currentDef = lang === DICT_LANGUAGE.VI ? definitionVN : definitionEN
		const existingTypeName =
			currentDef?.wordTypes?.[0]?.type || currentDef?.termWordTypes?.[0]?.type
		if (!existingTypeName) {
			return null
		}
		return (dictWordTypes || []).find(
			item =>
				item?.isTypeLanguage === lang &&
				(item?.type || '').trim().toLowerCase() ===
					existingTypeName.trim().toLowerCase()
		)
	}

	const handleGenerateAllWithAI = async () => {
		const word = (form.getFieldValue('word') || '').trim()
		if (!word) {
			showMessageError('Please enter word first')
			return
		}
		if (!dictWord?.id) {
			showMessageError('Please save word first before generating all content')
			return
		}

		const viType =
			getExistingTypeByLang(DICT_LANGUAGE.VI) ||
			getDefaultTypeByLang(DICT_LANGUAGE.VI)
		const enType =
			getExistingTypeByLang(DICT_LANGUAGE.EN) ||
			getDefaultTypeByLang(DICT_LANGUAGE.EN)
		if (!viType || !enType) {
			showMessageError('Missing dictionary types for Vietnamese/English')
			return
		}

		try {
			setIsGeneratingAll(true)
			const [viDefRs, enDefRs, exRs] = await Promise.all([
				api.agentsAdminApi.generateDictionaryDefinition({
					word,
					wordType: viType.type,
					lang: DICT_LANGUAGE.VI,
				}),
				api.agentsAdminApi.generateDictionaryDefinition({
					word,
					wordType: enType.type,
					lang: DICT_LANGUAGE.EN,
				}),
				api.agentsAdminApi.generateDictionaryExample({
					word,
					lang: DICT_LANGUAGE.VI,
				}),
			])

			const viDefinition = (viDefRs?.definition || '').trim()
			const enDefinition = (enDefRs?.definition || '').trim()
			const example = (exRs?.example || '').trim()
			const translate = (exRs?.translate || '').trim()
			if (!viDefinition || !enDefinition || !example || !translate) {
				showMessageError('AI returned incomplete data for generate-all action')
				return
			}

			await Promise.all([
				api.dictWordMeaningApi.create({
					dictWordID: dictWord.id,
					dictWordTypeID: viType.id,
					definition: viDefinition,
				}),
				api.dictWordMeaningApi.create({
					dictWordID: dictWord.id,
					dictWordTypeID: enType.id,
					definition: enDefinition,
				}),
				api.dictWordExampleApi.create({
					wordID: dictWord.id,
					example,
					translate,
				}),
			])

			dispatch(onReloadDefinitionWithoutLoading(null))
			showMessageSuccess('Generated definition + example successfully')
		} catch (err) {
			showMessageError(
				err?.response?.data || err?.message || 'Failed to generate all content'
			)
		} finally {
			setIsGeneratingAll(false)
		}
	}

	return (
		<Form form={form} initialValues={dictWord}>
			<Form.Item
				label="Word"
				name="word"
				rules={[
					{
						required: true,
						message: 'Please enter word',
					},
				]}
			>
				<Input ref={inputRef} placeholder={`Enter word`} />
			</Form.Item>

			<div className="u-row-right" style={{ marginTop: -8, marginBottom: 8 }}>
				<Button
					size="small"
					type="default"
					style={{ marginRight: 8 }}
					loading={isGeneratingAll}
					onClick={handleGenerateAllWithAI}
				>
					Generate definition + example with AI
				</Button>
				<Button
					size="small"
					type="default"
					loading={isGeneratingSpelling}
					onClick={handleGenerateBothSpellings}
				>
					Generate spelling US & spelling UK
				</Button>
			</div>

			<Form.Item label="Spelling" name="spelling">
				<Input placeholder={`Enter spelling`} />
			</Form.Item>

			<Form.Item label="Spelling Uk" name="spellingUk">
				<Input placeholder={`Enter spelling Uk`} />
			</Form.Item>

			<div className="u-row-right">
				<ButtonCommon
					onClick={handleSubmit}
					type="primary"
					loading={isSubmitLoading}
				>
					Save
				</ButtonCommon>
			</div>
		</Form>
	)
}

export default FormEditWord
